import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import recoverUsernamePassword from "@NA/Handlers/ADIRecoverUsernamePasswordHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import React, { useEffect, useState } from "react";
import { getRememberedUserName } from "@NA/Utils/Utility";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getStatuscall } from "@NA/Services/ADIgetStatusCall"; // This import is being used by P21
import { clearCoveoTokenAndOrg } from "@NA/Services/ADICoveoService";
import Hidden from "@insite/mobius/Hidden";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    const parsedQuery = parseQueryString<{ externalError?: string }>(getLocation(state).search);
    // const isPLP = getCurrentPage(state).type; code from Spire Prod
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        externalError: parsedQuery.externalError,
        session: state.context.session,
        currentLocation: getLocation(state),
        // isPLP, // code from spire Prod.
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    signIn,
    recoverUsernamePassword,
};

export interface SignInExistingAccountStyles {
    form?: InjectableCss;
    heading?: TypographyPresentationProps;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    signInGridContainerMobile?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    formErrorMessage?: InjectableCss;
    headingBorderBottom: InjectableCss;
    requirementsTextWrapper: InjectableCss;
    requirementsText: TypographyPresentationProps;
    requirementsErrorText: TypographyPresentationProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    form: {
        css: css`
            background: ${ADIColors.white1};
            border-radius: 5px;
            margin-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0px;
                    `,
                    css`
                        margin-right: 0px;
                    `,
                    null,
                    null,
                    null,
                ])};
        `,
    },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h4",
    },
    userNameGridItem: {
        width: 12,
        css: css`
            padding-left: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        > ${FormFieldStyle} {
                            align-items: center;
                            flex-direction: column;

                            > label {
                                display: flex;
                                flex-basis: 40%;
                                margin-right: 24px;
                                margin-left: 25px;
                                justify-content: flex-start;
                                > span {
                                    margin-left: 3px;
                                }
                            }
                            > ${FormInputWrapper} {
                                display: flex;
                                flex-basis: 100%;
                                max-width: 100%;
                            }
                        }
                    `,
                    css`
                        > ${FormFieldStyle} {
                            flex-direction: row;
                            > label {
                                display: flex;
                                flex-basis: 40%;
                                max-width: 40%;
                                margin-right: 30px;
                                justify-content: flex-end;
                                > span {
                                    margin-left: 3px;
                                }
                            }
                            > ${FormInputWrapper} {
                                display: flex;
                                flex-basis: 100%;
                                max-width: 100%;
                            }
                        }
                    `,
                    css`
                        > ${FormFieldStyle} {
                            align-items: center;
                            flex-direction: row;
                            > label {
                                display: flex;
                                flex-basis: 30%;
                                max-width: 30%;
                                margin-right: 30px;
                                justify-content: flex-end;
                                > span {
                                    margin-left: 3px;
                                }
                            }
                            > ${FormInputWrapper} {
                                display: flex;
                                flex-basis: 70%;
                                max-width: 70%;
                                > input {
                                    width: 70%;
                                    height: 44px;
                                }
                            }
                        }
                    `,
                ])};
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            padding-left: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        > ${FormFieldStyle} {
                            align-items: center;
                            flex-direction: column;
                            > label {
                                display: flex;
                                flex-basis: 40%;
                                justify-content: flex-start;
                                margin-right: 24px;
                                margin-left: 25px;
                                > span {
                                    margin-left: 3px;
                                }
                            }
                            > ${FormInputWrapper} {
                                display: flex;
                                flex-basis: 100%;
                                max-width: 100%;
                            }
                        }
                    `,
                    css`
                        > ${FormFieldStyle} {
                            align-items: center;
                            flex-direction: row;
                            > label {
                                display: flex;
                                flex-basis: 40%;
                                max-width: 40%;
                                justify-content: flex-end;
                                margin-right: 30px;
                                > span {
                                    margin-left: 3px;
                                }
                            }
                            > ${FormInputWrapper} {
                                display: flex;
                                flex-basis: 100%;
                                max-width: 100%;
                            }
                        }
                    `,
                    css`
                        > ${FormFieldStyle} {
                            align-items: center;
                            flex-direction: row;
                            > label {
                                display: flex;
                                flex-basis: 30%;
                                max-width: 30%;
                                justify-content: flex-end;
                                margin-right: 30px;
                                > span {
                                    margin-left: 3px;
                                }
                            }
                            > ${FormInputWrapper} {
                                display: flex;
                                flex-basis: 70%;
                                max-width: 70%;
                                > input {
                                    width: 70%;
                                    height: 44px;
                                }
                                > button {
                                    position: relative;
                                    right: 35px;
                                }
                            }
                        }
                    `,
                ])};
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            padding-left: 50px;
            margin-top: -50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                        justify-content: left;
                    `,
                    css`
                        justify-content: center;
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                        justify-content: center;
                    `,
                ])};
        `,
    },
    rememberMeGridItem: {
        width: 12,
        css: css`
            padding: 0px 15px 8px;
            padding-left: 40px;
            margin-top: -20px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                        max-width: 100%;
                        justify-content: left;
                    `,
                    css`
                        max-width: 100%;
                        justify-content: center;
                        padding-left: 30px;
                    `,
                    css`
                        max-width: 85%;
                        justify-content: center;
                        padding-left: 30px;
                    `,
                ])};
        `,
    },
    forgotPasswordGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                        justify-content: left;
                    `,
                    css`
                        padding-left: 106px;
                        justify-content: center;
                    `,
                    css`
                        padding-left: 106px;
                        justify-content: center;
                    `,
                ])};
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 16,
            css: css`
                margin-top: -20px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            justify-content: center;
                            margin-right: -100px;
                        `,
                        css`
                            justify-content: center;
                            margin-right: -100px;
                        `,
                        css`
                            justify-content: center;
                            margin-right: 40px;
                        `,
                    ])};
            `,
        },
    },
    signInButton: {
        css: css`
            width: 100%;
            margin-top: -10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 160px;
                    `,
                    css`
                        width: 160px;
                    `,
                    css`
                        min-width: 180px;
                        width: auto;
                        margin-right: -70px;
                    `,
                    css`
                        min-width: 180px;
                        width: auto;
                        margin-right: 40px;
                        margin-left: 30px;
                    `,
                    css`
                        min-width: 180px;
                        width: auto;
                        margin-right: 40px;
                        margin-left: 30px;
                    `,
                ])}
        `,
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
    formErrorMessage: {
        css: css`
            margin-bottom: 15px;
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: left;
            color: ${ADIColors.danger};
            float: left;
            width: 100%;
            padding-left: 15px;
        `,
    },
    headingBorderBottom: {
        css: css`
            border-bottom: 2px solid ${ADIColors.gray15};
            width: auto%;
            margin: 24px 20px 24px 20px;
        `,
    },
    heading: {
        css: css`
            padding: 15px 0px 0px 20px;
            margin-bottom: 0px !important;
            text-align: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 15px 0px 0px 15px;
                    `,
                    css`
                        padding: 15px 0px 0px 15px;
                    `,
                    css`
                        padding: 15px 0px 0px 20px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    signInGridContainer: {
        css: css`
            > ${GridOffset} {
                width: 100%;
                margin: 0px auto 12px 0px;
                padding: 15px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0px auto 12px -20px;
                    `,
                    css`
                        margin: 0px auto 12px -20px;
                    `,
                    css``,
                    css`
                        padding-left: 65px;
                    `,
                    css`
                        padding-left: 65px;
                    `,
                ])}
        `,
    },
    signInGridContainerMobile: {
        css: css`
            > ${GridOffset} {
                width: 100%;
                margin: 0px auto 12px 0px;
                padding: 15px;
            }
        `,
    },
    requirementsTextWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 1rem;
        `,
    },
    requirementsText: {
        css: css`
            padding-left: 15px;
        `,
    },
    requirementsErrorText: {
        css: css`
            padding-left: 30px;
            color: ${ADIColors.errorFields};
        `,
    },
    rememberMeCheckbox: {
        css: css`
            > span {
                background: #fff;
                border: solid 1px #bbb;
            }
            > label {
                font-size: 16px;
            }
        `,
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

const ADISignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    externalError,
    session,

    currentLocation,
    recoverUsernamePassword,
    // isPLP, // code from spire prod.
    history,
}: Props) => {
    const [rememberedUserName, setRememberedUserName] = useState(getRememberedUserName());
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");

    const [isUserNameError, setIsUserNameError] = useState(false);
    const [isPasswordError, setPasswordError] = useState(false);
    const [isGlobalError, setIsGlobalError] = useState(false);

    const [signInError, setSignInError] = useState("");
    const [loginAttempt, setLoginAttempt] = useState(1);
    const [disableSignIn, setDisableSignIn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showCustomerStatusBanner, setShowCustomerStatusBanner] = useState<boolean>(); // P21 change
    const [isMobile, setIsMobile] = React.useState(false);

    const signInFailed = async (error: any) => {
        setSignInError(error.message);
        setLoginAttempt(loginAttempt => loginAttempt + 1);
        setDisableSignIn(false);

        if (loginAttempt >= 2) {
            if (typeof window !== "undefined" && window.localStorage) {
                window.localStorage.setItem("userNameSA", userName);
            }

            recoverUsernamePassword({
                userEmail: "noCaptchaValidation",
                passwordEmail: userName,
                isPasswordReset: true,
                isuserNameReset: false,
                userNotFound: false,
                isSuccess: false,
                uri: "",
                properties: { captchaResponse: "", isEmailSend: true },
                onSuccess: (result: any) => {
                    window.localStorage.setItem("isUserNameError", result.result.userNotFound);
                    window.location.href = "/MyAccount/signinassist";
                },
            });
        }
    };

    React.useEffect(() => {
        if (!window) return;
        const onWindowResize = () => {
            if (window.innerWidth <= 768) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };
        onWindowResize();
    }, []);

    const showRememberMe = accountSettings.rememberMe;

    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        const user = (userName || rememberedUserName) as string;
        setIsSignInClicked(true);

        !user ? setIsUserNameError(true) : setIsUserNameError(false);

        !pass ? setPasswordError(true) : setPasswordError(false);
        const isGlobalErrorOccured = !user || !pass;

        setIsGlobalError(isGlobalErrorOccured);

        if (isGlobalErrorOccured) return;

        if (accountSettings.useEmailAsUserName && !validateEmail(user)) return;

        let returnUrl = currentLocation.pathname + currentLocation.search;

        setErrorMessage("");
        localStorage.removeItem("sessionStartTime");
        signIn({
            userName: user,
            password: pass,
            rememberMe,
            returnUrl,
            // skipRedirect: !!(isPLP && isPLP === "ProductListPage"), // code from spire prod
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                } else if (statusCode === 400) {
                    // added this condition in P21
                    setErrorMessage(error);
                    signInFailed(error);
                    return;
                } else {
                    setErrorMessage(error);
                    signInFailed(error);
                }
            },
            onComplete() {
                // redirectofRequestQuoteForm(returnUrl);  // commented this code in p21
            },
        });
    };

    // Below function is being used in P21
    async function getStatusCall() {
        await getStatuscall()
            .then(response => {
                const showCustomerStatusBanner = (response as any)?.response?.showCustomerStatusBanner;
                setShowCustomerStatusBanner(showCustomerStatusBanner);
                localStorage.setItem("showCustomerStatusBanner", showCustomerStatusBanner);
            })
            .catch(err => {
                return err;
            });
    }

    // The below commented code is from Spire Prod.
    // const redirectofRequestQuoteForm = (inputURl:string) => {
    //     if(inputURl.toLowerCase().includes('myaccount%2frequestquoteform')){
    //         history.push('/MyAccount/RequestQuoteForm');
    //         }
    // }

    useEffect(() => {
        if (session.isAuthenticated) {
           // getStatusCall(); // P21 Change
            const dataLayer = (window as any).dataLayer || [];
            const userId = session && session.userProfileId;

            clearCoveoTokenAndOrg();
            if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: "Successful login",
                    ecommerce: {
                        authenticationState: "Logged In",
                        userId: userId || "",
                    },
                });
            }
            return dataLayer;
        }
    }, [session.isAuthenticated]);

    useEffect(() => {
        if (rememberedUserName) localStorage.setItem("rememberedUserName", rememberedUserName);
    }, [rememberedUserName]);

    useEffect(() => {
        const rememberMeValue = localStorage.getItem("rememberMe");
        setRememberMe(rememberMeValue === "true" ? true : false);
    }, []);

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
        if (rememberMe) {
            localStorage.setItem("rememberMe", rememberMe.toString());
            localStorage.setItem("rememberedUserName", userName);
        } else {
            localStorage.setItem("rememberMe", rememberMe.toString());
        }
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? "*Please enter information in required fields"
                    : errorMessage || "";
            }
        }

        return "";
    };

    const forgetPasswordHandler = () => {
        if (!isAuthenticated) {
            history.push("/MyAccount/recoverusernamepassword");
        }
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? "*Please enter information in required fields" : errorMessage || "";

    return (
        <>
            <LoadingOverlay loading={isLoading} spinnerProps={{ color: "primary" }}>
                <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                    <Typography {...styles.heading} variant="h3">
                        {translate("Sign In")}
                    </Typography>
                    <Hidden below="lg">
                        <Typography
                            style={{
                                color: isGlobalError ? ADIColors.danger : "#2D2926",
                                paddingLeft: "20px",
                                fontSize: "14px",
                            }}
                        >
                            {translate("With your username and password.")}
                        </Typography>
                        <StyledWrapper {...styles.headingBorderBottom}></StyledWrapper>
                    </Hidden>

                    <StyledWrapper {...styles.requirementsTextWrapper}>
                        {passwordErrorMessage || userErrorMessage ? (
                            <Typography {...styles.formErrorMessage}>{passwordErrorMessage}</Typography>
                        ) : (
                            <Typography
                                style={{
                                    color: isGlobalError ? ADIColors.danger : "#2D2926",
                                    paddingLeft: isMobile ? "15px" : "20px",
                                    fontSize: "14px",
                                }}
                            >
                                {translate("* Please enter information in required fields.")}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <Hidden above="sm">
                        <GridContainer {...styles.signInGridContainerMobile}>
                            <GridItem {...styles.userNameGridItem}>
                                <TextField
                                    id="userName"
                                    {...styles.userNameTextField}
                                    labelProps={{
                                        css: css`
                                            color: ${isUserNameError ? ADIColors.danger : "#2D2926"};
                                        `,
                                    }}
                                    label={
                                        accountSettings.useEmailAsUserName
                                            ? translate("Username")
                                            : translate("Username")
                                    }
                                    onChange={e => {
                                        e.currentTarget.value.length > 0
                                            ? setIsUserNameError(false)
                                            : setIsUserNameError(true);
                                        setUserName(e.currentTarget.value);
                                        setRememberedUserName("");
                                    }}
                                    value={rememberedUserName || userName}
                                    // error={userErrorMessage}
                                    autoComplete="username"
                                    data-test-selector="signIn_userName"
                                    placeholder="Email Address"
                                    required
                                    maxLength={75}
                                    style={{ borderColor: isUserNameError ? ADIColors.danger : ADIColors.gray3 }}
                                />
                            </GridItem>
                            <GridItem {...styles.passwordGridItem} style={{ marginTop: -18 }}>
                                <TextField
                                    id="password"
                                    {...styles.passwordTextField}
                                    label={translate("Password ")}
                                    labelProps={{
                                        css: css`
                                            color: ${isPasswordError ? ADIColors.danger : "#2D2926"};
                                        `,
                                    }}
                                    maxLength={75}
                                    onChange={e => {
                                        e.currentTarget.value.length > 0
                                            ? setPasswordError(false)
                                            : setPasswordError(true);
                                        setPassword(e.currentTarget.value);
                                    }}
                                    value={password}
                                    required
                                    clickableText={password}
                                    autoComplete="current-password"
                                    data-test-selector="signIn_password"
                                    type="password"
                                    placeholder="Password"
                                    style={{ borderColor: isPasswordError ? ADIColors.danger : ADIColors.gray3 }}
                                />
                            </GridItem>

                            <GridItem {...styles.rememberMeGridItem}>
                                {showRememberMe && (
                                    <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                        <Checkbox
                                            uid="rememberMe"
                                            {...styles.rememberMeCheckbox}
                                            checked={rememberMe}
                                            onChange={(e, value) => setRememberMe(value)}
                                        >
                                            {translate("Remember Me")}
                                        </Checkbox>
                                    </CheckboxGroup>
                                )}
                            </GridItem>
                            <GridItem {...styles.signInButtonGridItem}>
                                <Button
                                    {...styles.signInButton}
                                    disabled={disableSignIn}
                                    type="submit"
                                    data-test-selector="signIn_submit"
                                >
                                    {translate("Sign In")}
                                </Button>
                                <Modal
                                    headline={translate("Change Password")}
                                    {...styles.changePasswordModal}
                                    isOpen={isChangePasswordModalOpen}
                                    handleClose={onChangePasswordClose}
                                >
                                    <SignInChangePasswordForm
                                        enteredUserName={userName}
                                        enteredPassword={password}
                                        instructionsText={fields.changePasswordInstructions}
                                        onPasswordChanged={onPasswordChanged}
                                    />
                                </Modal>
                            </GridItem>
                            <GridItem {...styles.forgotPasswordGridItem}>
                                <Link
                                    {...styles.forgotPasswordLink}
                                    type="button"
                                    onClick={forgetPasswordHandler}
                                    data-test-selector="signIn_forgotPassword"
                                >
                                    {translate("Forgot your username or password?")}
                                </Link>
                            </GridItem>
                        </GridContainer>
                    </Hidden>

                    <Hidden below="sm">
                        <GridContainer {...styles.signInGridContainer}>
                            <GridItem {...styles.userNameGridItem} style={{ paddingTop: "0px" }}>
                                <TextField
                                    id="userName"
                                    {...styles.userNameTextField}
                                    labelProps={{
                                        css: css`
                                            color: ${isUserNameError ? ADIColors.danger : "#2D2926"};
                                        `,
                                    }}
                                    label={
                                        accountSettings.useEmailAsUserName
                                            ? translate("User Name")
                                            : translate("User Name")
                                    }
                                    onChange={e => {
                                        e.currentTarget.value.length > 0
                                            ? setIsUserNameError(false)
                                            : setIsUserNameError(true);
                                        setUserName(e.currentTarget.value);
                                        setRememberedUserName("");
                                    }}
                                    value={rememberedUserName || userName}
                                    // error={userErrorMessage}
                                    autoComplete="username"
                                    data-test-selector="signIn_userName"
                                    placeholder="Email Address"
                                    required
                                    maxLength={75}
                                    style={{ borderColor: isUserNameError ? ADIColors.danger : ADIColors.gray3 }}
                                />
                            </GridItem>
                            <GridItem {...styles.passwordGridItem} style={{ marginTop: -18 }}>
                                <TextField
                                    id="password"
                                    {...styles.passwordTextField}
                                    label={translate("Password")}
                                    labelProps={{
                                        css: css`
                                            color: ${isPasswordError ? ADIColors.danger : "#2D2926"};
                                        `,
                                    }}
                                    maxLength={75}
                                    onChange={e => {
                                        e.currentTarget.value.length > 0
                                            ? setPasswordError(false)
                                            : setPasswordError(true);
                                        setPassword(e.currentTarget.value);
                                    }}
                                    value={password}
                                    required
                                    clickableText={password}
                                    autoComplete="current-password"
                                    data-test-selector="signIn_password"
                                    type="password"
                                    placeholder="Password"
                                    style={{ borderColor: isPasswordError ? ADIColors.danger : ADIColors.gray3 }}
                                />
                            </GridItem>

                            <GridItem {...styles.rememberMeGridItem}>
                                {showRememberMe && (
                                    <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                        <Checkbox
                                            uid="rememberMe"
                                            {...styles.rememberMeCheckbox}
                                            checked={rememberMe}
                                            onChange={(e, value) => setRememberMe(value)}
                                        >
                                            {translate("Remember Me")}
                                        </Checkbox>
                                    </CheckboxGroup>
                                )}
                            </GridItem>
                            <GridItem {...styles.signInButtonGridItem}>
                                <Button
                                    {...styles.signInButton}
                                    disabled={disableSignIn}
                                    type="submit"
                                    data-test-selector="signIn_submit"
                                >
                                    {translate("Sign In")}
                                </Button>
                                <Modal
                                    headline={translate("Change Password")}
                                    {...styles.changePasswordModal}
                                    isOpen={isChangePasswordModalOpen}
                                    handleClose={onChangePasswordClose}
                                >
                                    <SignInChangePasswordForm
                                        enteredUserName={userName}
                                        enteredPassword={password}
                                        instructionsText={fields.changePasswordInstructions}
                                        onPasswordChanged={onPasswordChanged}
                                    />
                                </Modal>
                            </GridItem>
                            <GridItem {...styles.forgotPasswordGridItem}>
                                <Link
                                    {...styles.forgotPasswordLink}
                                    type="button"
                                    onClick={forgetPasswordHandler}
                                    data-test-selector="signIn_forgotPassword"
                                >
                                    {translate("Forgot your username or password?")}
                                </Link>
                            </GridItem>
                        </GridContainer>
                    </Hidden>
                </StyledForm>
            </LoadingOverlay>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADISignInExistingAccount)),
    definition: {
        group: "Sign In",
        icon: "LogIn",
        displayName: "ADI SignIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
        ],
    },
};

export default widgetModule;
